import React, { useCallback, useState } from 'react';

import Cookie from 'js-cookie';

import { resolveLabels } from './messages';

/** Validade do cookie de tema, em dias — a escolha é preferência, não sessão. */
const COOKIE_EXPIRES_DAYS = 3650;

const IMAGES_BASE = '/assets/images/neo-structure/utils';

export interface IThemeSwitchProps {
    /** Nome do cookie de tema (`AST_THEME_COOKIE` do legado). */
    cookieName: string;
    /** Valor gravado no cookie para o tema escuro (`AST_THEME_DARK`). */
    darkValue: string;
    /** Valor gravado no cookie para o tema claro (`AST_THEME_LIGHT`). */
    lightValue: string;
    /** Classe que o PHP escreve no `<body>` quando o tema é escuro. */
    bodyClass: string;
    /** Idioma da página, no código do legado (`pt_br`, `en_us`, ...). */
    lang?: string;
}

/**
 * Switch de tema da página de conteúdo.
 *
 * O tema é decidido NO SERVIDOR — `contentThemeBodyClass()` escreve a classe no `<body>` antes
 * de a página sair do PHP. Por isso o estado inicial vem da CLASSE do `<body>`, não do
 * `prefers-color-scheme` nem do cookie: decidir no cliente reintroduz o flash.
 *
 * @example
 * <theme-switch data-cookie="ast_theme" data-body-class="theme-dark"></theme-switch>
 */
const ThemeSwitch: React.FC<IThemeSwitchProps> = ({
    cookieName,
    darkValue,
    lightValue,
    bodyClass,
    lang
}) => {
    const [isDark, setIsDark] = useState<boolean>(
        () => document.body.classList.contains(bodyClass)
    );

    const labels = resolveLabels(lang);

    const handleToggle = useCallback(() => {
        setIsDark((current) => {
            const next = !current;

            document.body.classList.toggle(bodyClass, next);
            Cookie.set(cookieName, next ? darkValue : lightValue, {
                expires: COOKIE_EXPIRES_DAYS,
                path: '/'
            });

            return next;
        });
    }, [bodyClass, cookieName, darkValue, lightValue]);

    return (
        <div className="theme-switch">
            <button
                type="button"
                role="switch"
                className="theme-switch__button"
                aria-checked={!isDark}
                aria-label={labels.action}
                onClick={handleToggle}
            >
                <span className="theme-switch__thumb" />

                <img className="theme-switch__clouds" src={`${IMAGES_BASE}/bt-light-bg-clouds.svg`} alt="" />
                <img className="theme-switch__star" src={`${IMAGES_BASE}/bt-dark-star.svg`} alt="" />
                <img
                    className="theme-switch__star theme-switch__star--second"
                    src={`${IMAGES_BASE}/bt-dark-star.svg`}
                    alt=""
                />
                <img
                    className="theme-switch__star theme-switch__star--third"
                    src={`${IMAGES_BASE}/bt-dark-star.svg`}
                    alt=""
                />
            </button>

            <span className="theme-switch__label">{isDark ? labels.dark : labels.light}</span>
        </div>
    );
};

export default ThemeSwitch;
